import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Camera, MapPin, User, CheckCircle2, ChevronRight, Image as ImageIcon, Trash2, Tag as TagIcon, Check, Search } from 'lucide-react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

const dialCodes = ["971", "91", "1", "44", "61", "81", "33", "49", "7", "86", "39", "31", "34", "41", "43", "45", "46", "47", "48", "51", "52", "53", "54", "55", "56", "57", "58", "60", "62", "63", "64", "65", "66", "82", "84", "90", "92", "93", "94", "95", "98", "212", "351", "352", "353", "354", "358", "372", "370", "371", "420", "421", "380", "381", "385", "386", "387", "389", "423", "356", "357", "359", "20", "27"];

const PhoneInputComponent = ({ defaultCountryCode, defaultPhoneNumber, onChange }) => {
  const [countryCode, setCountryCode] = useState(defaultCountryCode || '+91');
  const [phoneNumber, setPhoneNumber] = useState(defaultPhoneNumber || '');
  const [countryData, setCountryData] = useState({ dialCode: '91' });

  useEffect(() => {
    onChange({ target: { name: 'country_code', value: countryCode } });
    onChange({ target: { name: 'phone_number', value: phoneNumber } });
  }, [countryCode, phoneNumber, onChange]);

  const handlePhoneChange = (val, data) => {
    setCountryData(data);
    const dial = data.dialCode;
    let cleaned = val;
    if (val.startsWith(dial)) {
      cleaned = val.slice(dial.length);
    }
    
    const formattedCode = dial.startsWith('+') ? dial : `+${dial}`;
    setCountryCode(formattedCode);
    setPhoneNumber(cleaned);
  };

  const getPhoneInputValue = () => {
    const dial = (countryCode || countryData.dialCode).replace(/\+/g, '');
    const num = phoneNumber || "";
    return dial + num;
  };

  return (
    <div className="flex gap-3 w-full">
      <div className="bg-gray-50 border border-gray-100 shadow-sm rounded-2xl px-4 flex items-center gap-2 min-w-[90px] justify-center transition-all h-[46px] focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary">
        <div className="phone-flag-wrapper scale-[1.15] flex items-center">
          <PhoneInput
            country={countryCode?.replace(/\+/g, '') === '91' ? 'in' : undefined}
            value={getPhoneInputValue()}
            onChange={handlePhoneChange}
            containerClass="!w-auto !border-none [&_.arrow]:hidden [&_.selected-flag:hover]:!bg-transparent"
            inputClass="!hidden"
            buttonClass="!bg-transparent hover:!bg-transparent !border-none !relative !p-0 !m-0 !w-6 !h-4"
            dropdownClass="!rounded-2xl !mt-2 !border-gray-100 !shadow-xl !z-[110]"
            searchClass="!rounded-xl !mx-2 !my-2 !border-gray-100"
            enableSearch={true}
            disableSearchIcon={true}
          />
        </div>
        <span className="text-sm font-black text-gray-700">
          {countryCode?.startsWith('+') ? countryCode : `+${countryCode || countryData.dialCode}`}
        </span>
      </div>

      <input
        type="text"
        value={phoneNumber}
        onChange={(e) => {
          let raw = e.target.value;
          let val = raw.replace(/\D/g, '');
          
          if (raw.trim().startsWith('+')) {
            const cleanedRaw = raw.replace(/[^\d+]/g, ''); 
            const matchedDial = dialCodes
              .sort((a, b) => b.length - a.length)
              .find(code => cleanedRaw.startsWith('+' + code));
            
            if (matchedDial) {
              const localNum = cleanedRaw.slice(matchedDial.length + 1);
              setCountryCode('+' + matchedDial);
              setPhoneNumber(localNum);
              return;
            }
          }

          const dial = (countryCode || countryData.dialCode).replace(/\+/g, '');
          if (val.startsWith(dial) && val.length > dial.length + 5) {
            val = val.slice(dial.length);
          }
          setPhoneNumber(val);
        }}
        placeholder="Phone number"
        className="flex-1 w-full px-5 bg-gray-50 border border-gray-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all font-medium h-[46px]"
      />
    </div>
  );
};

const ShopProfile = () => {
  const { userData, setUserData } = useOutletContext();
  const [activeTab, setActiveTab] = useState('basic');
  const [isLoading, setIsLoading] = useState(false);
  
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const currentPasswordRef = useRef(null);
  const newPasswordRef = useRef(null);
  
  const logoInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const formDataRef = useRef(userData || {});

  const [businessHoursState, setBusinessHoursState] = useState({
    openingTime: '',
    closingTime: ''
  });
  
  const [mapUrlInput, setMapUrlInput] = useState('');
  const [latitudeInput, setLatitudeInput] = useState('');
  const [longitudeInput, setLongitudeInput] = useState('');

  const [tagsList, setTagsList] = useState([]);
  const [isTagsLoading, setIsTagsLoading] = useState(false);
  const [selectedSecondaryTags, setSelectedSecondaryTags] = useState([]);
  const [tagSearchQuery, setTagSearchQuery] = useState('');
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BASE_URL}/location/dropdown/list`)
      .then((response) => response.json())
      .then((data) => {
        setLocations(data.data || []);
      })
      .catch((error) => console.error("Error fetching locations:", error));
  }, []);

  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  // Fetch Tags from Backend API (/api/tags?type=shop)
  useEffect(() => {
    const fetchShopTags = async () => {
      setIsTagsLoading(true);
      try {
        const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/tags?type=shop`);
        const allTags = response.data || [];
        // Strictly filter for non-primary shop tags only (partners only manage secondary tags)
        const shopOnlyTags = allTags.filter(t => {
          if (t.isPrimary) return false;
          if (!t.type) return false;
          if (Array.isArray(t.type)) return t.type.includes('shop');
          return t.type === 'shop';
        }).filter(t => t.isActive !== false);

        setTagsList(shopOnlyTags);
      } catch (error) {
        console.error('Error fetching shop tags:', error);
      } finally {
        setIsTagsLoading(false);
      }
    };

    fetchShopTags();
  }, []);

  useEffect(() => {
    if (userData) {
      formDataRef.current = { ...userData };
      setBusinessHoursState({
        openingTime: userData.businessHours?.openingTime || '',
        closingTime: userData.businessHours?.closingTime || ''
      });
      setMapUrlInput(userData.map_url || '');
      setLatitudeInput(userData.latitude || '');
      setLongitudeInput(userData.longitude || '');

      if (userData.secondary_tags && Array.isArray(userData.secondary_tags)) {
        const ids = userData.secondary_tags.map(t => typeof t === 'object' && t ? (t._id || t.id) : t).filter(Boolean);
        setSelectedSecondaryTags(ids);
      }
    }
  }, [userData]);

  const toggleSecondaryTag = (tagId) => {
    setSelectedSecondaryTags(prev => {
      const exists = prev.includes(tagId);
      const updated = exists ? prev.filter(id => id !== tagId) : [...prev, tagId];
      
      const refToUpdate = formDataRef?.current ? formDataRef.current : userData;
      refToUpdate.secondary_tags = updated;
      if (formDataRef) formDataRef.current.secondary_tags = updated;

      return updated;
    });
  };

  const handleExtractCoordinates = (urlToExtract) => {
    const url = urlToExtract || mapUrlInput || '';
    if (!url || !url.trim()) {
      toast.error('Please enter or paste a Google Maps URL first.');
      return;
    }

    let lat = '';
    let lng = '';

    const atMatch = url.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
    if (atMatch) {
      lat = atMatch[1];
      lng = atMatch[2];
    }

    if (!lat || !lng) {
      const dMatch = url.match(/!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/);
      if (dMatch) {
        lat = dMatch[1];
        lng = dMatch[2];
      }
    }

    if (!lat || !lng) {
      const queryMatch = url.match(/[?&](?:q|ll)=(?:loc:)?(-?\d+\.\d+),(-?\d+\.\d+)/);
      if (queryMatch) {
        lat = queryMatch[1];
        lng = queryMatch[2];
      }
    }

    if (!lat || !lng) {
      const genericMatch = url.match(/(-?\d{1,2}\.\d+)[,\s]+(-?\d{1,3}\.\d+)/);
      if (genericMatch) {
        lat = genericMatch[1];
        lng = genericMatch[2];
      }
    }

    if (lat && lng) {
      setLatitudeInput(lat);
      setLongitudeInput(lng);

      if (formDataRef) {
        formDataRef.current.latitude = parseFloat(lat);
        formDataRef.current.longitude = parseFloat(lng);
        formDataRef.current.map_url = url;
      }
      if (setUserData) {
        setUserData(prev => ({
          ...prev,
          latitude: parseFloat(lat),
          longitude: parseFloat(lng),
          map_url: url
        }));
      }

      toast.success(`Extracted! Latitude: ${lat}, Longitude: ${lng}`);
    } else {
      toast.error('Could not extract coordinates. Please check the URL or type coordinates manually.');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.startsWith('businessHours.')) {
      const field = name.split('.')[1];
      formDataRef.current = {
        ...formDataRef.current,
        businessHours: {
          ...formDataRef.current.businessHours,
          [field]: value
        }
      };
      setBusinessHoursState(prev => ({ ...prev, [field]: value }));
    } else {
      formDataRef.current = {
        ...formDataRef.current,
        [name]: type === 'checkbox' ? checked : value
      };
      if (name === 'owner_name') {
        formDataRef.current.name = value;
      }
    }
  };

  const handleDaysChange = (event) => {
    const { value, checked } = event.target;
    const updatedDays = checked
      ? [...(formDataRef.current.businessHours?.days || []), value]
      : (formDataRef.current.businessHours?.days || []).filter((day) => day !== value);

    formDataRef.current = {
      ...formDataRef.current,
      businessHours: {
        ...formDataRef.current.businessHours,
        days: updatedDays
      }
    };
    setUserData(prev => ({
      ...prev,
      businessHours: {
        ...prev.businessHours,
        days: updatedDays
      }
    }));
  };

  const uploadImage = async (file) => {
    if (!file) return null;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await axios.post(
        'https://4trip-server.trrip.live/api/upload/single',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data.fileUrl;
    } catch (error) {
      toast.error('Failed to upload image');
      return null;
    }
  };

  const handleImageUpload = async (files) => {
    const uploadPromises = Array.from(files).map((file) => uploadImage(file));
    try {
      const urls = await Promise.all(uploadPromises);
      const newImageUrls = [
        ...(formDataRef.current.image_url || []),
        ...urls.filter((url) => url !== null)
      ];
      formDataRef.current.image_url = newImageUrls;
      setUserData((prev) => ({ ...prev, image_url: newImageUrls }));
    } catch (error) {
      toast.error('Failed to upload some images');
    }
  };

  const handleLogoUpload = async (file) => {
    const url = await uploadImage(file);
    if (url) {
      formDataRef.current.logo_url = url;
      setUserData((prev) => ({ ...prev, logo_url: url }));
    }
  };

  const removeImage = (index) => {
    const updatedImages = [...(formDataRef.current.image_url || [])];
    updatedImages.splice(index, 1);
    formDataRef.current.image_url = updatedImages;
    setUserData((prev) => ({ ...prev, image_url: updatedImages }));
  };

  const togglePasswordFields = useCallback(() => {
    setShowPasswordFields((prev) => !prev);
  }, []);

  const handleSaveChanges = async () => {
    setIsLoading(true);
    const token = localStorage.getItem('token_partner_shop');
    const id = localStorage.getItem('id_partner_shop');
    const updateData = { ...formDataRef.current };

    if (showPasswordFields && currentPasswordRef.current?.value && newPasswordRef.current?.value) {
      updateData.currentPassword = currentPasswordRef.current.value;
      updateData.newPassword = newPasswordRef.current.value;
    }

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/auth/${id}/`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.status === 200) {
        toast.success('Shop details updated successfully!');
        setUserData(formDataRef.current);
        setShowPasswordFields(false);
        if (currentPasswordRef.current) currentPasswordRef.current.value = '';
        if (newPasswordRef.current) newPasswordRef.current.value = '';
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update shop details.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!userData) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const currentData = formDataRef.current;

  return (
    <div className="space-y-6 max-w-6xl mx-auto pb-12 animate-page-entry">
      {/* Top Banner & Title Bar */}
      <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-primary/5 flex items-center justify-center text-primary font-black shadow-inner border border-primary/10 overflow-hidden">
            {currentData.logo_url ? (
               <img src={currentData.logo_url} alt="Logo" className="w-full h-full object-cover" />
            ) : (
               currentData.business_name?.charAt(0).toUpperCase() || 'S'
            )}
          </div>
          <div>
            <h1 className="text-2xl font-black text-gray-900 tracking-tight flex items-center gap-2">
              Shop Profile
              <CheckCircle2 className="w-5 h-5 text-emerald-500" />
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <p className="text-sm font-bold text-gray-500">{currentData.business_name || 'My Shop'}</p>
              <span className="w-1 h-1 rounded-full bg-gray-300"></span>
              <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">{currentData.email}</p>
            </div>
          </div>
        </div>
        <button
          onClick={handleSaveChanges}
          disabled={isLoading}
          className={`px-8 py-3.5 rounded-2xl font-black text-white shadow-xl flex items-center gap-3 transition-all ${
            isLoading 
            ? 'bg-primary/50 cursor-wait' 
            : 'bg-primary hover:bg-primary/90 hover:scale-105 active:scale-95 shadow-primary/30'
          }`}
        >
          {isLoading ? (
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <>
              Save Changes
              <ChevronRight className="w-4 h-4" />
            </>
          )}
        </button>
      </div>

      {/* TAB NAVIGATION BAR (Full Section Width) */}
      <div className="w-full p-1 mb-2">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 w-full">
          <button
            type="button"
            onClick={() => setActiveTab('basic')}
            className={`w-full flex items-center justify-center gap-2 px-3 sm:px-4 py-3 rounded-2xl text-xs font-black uppercase tracking-wider transition-all cursor-pointer ${
              activeTab === 'basic'
                ? 'bg-primary text-white shadow-md shadow-primary/20 ring-2 ring-primary'
                : 'bg-white text-gray-500 hover:text-gray-900 hover:bg-gray-100/70 border border-gray-200/60 shadow-2xs'
            }`}
          >
            <User size={16} className="shrink-0" />
            <span className="truncate">Basic Info & Logo</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('images')}
            className={`w-full flex items-center justify-center gap-2 px-3 sm:px-4 py-3 rounded-2xl text-xs font-black uppercase tracking-wider transition-all cursor-pointer ${
              activeTab === 'images'
                ? 'bg-primary text-white shadow-md shadow-primary/20 ring-2 ring-primary'
                : 'bg-white text-gray-500 hover:text-gray-900 hover:bg-gray-100/70 border border-gray-200/60 shadow-2xs'
            }`}
          >
            <Camera size={16} className="shrink-0" />
            <span className="truncate">Images & Gallery</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('location')}
            className={`w-full flex items-center justify-center gap-2 px-3 sm:px-4 py-3 rounded-2xl text-xs font-black uppercase tracking-wider transition-all cursor-pointer ${
              activeTab === 'location'
                ? 'bg-primary text-white shadow-md shadow-primary/20 ring-2 ring-primary'
                : 'bg-white text-gray-500 hover:text-gray-900 hover:bg-gray-100/70 border border-gray-200/60 shadow-2xs'
            }`}
          >
            <MapPin size={16} className="shrink-0" />
            <span className="truncate">Address & Operating Hours</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('tags')}
            className={`w-full flex items-center justify-center gap-2 px-3 sm:px-4 py-3 rounded-2xl text-xs font-black uppercase tracking-wider transition-all cursor-pointer ${
              activeTab === 'tags'
                ? 'bg-primary text-white shadow-md shadow-primary/20 ring-2 ring-primary'
                : 'bg-white text-gray-500 hover:text-gray-900 hover:bg-gray-100/70 border border-gray-200/60 shadow-2xs'
            }`}
          >
            <TagIcon size={16} className="shrink-0" />
            <span className="truncate">Shop Tags</span>
          </button>
        </div>
      </div>

      {/* TAB 1: BASIC INFO & LOGO */}
      {activeTab === 'basic' && (
        <div key="basic" className="bg-white p-6 sm:p-8 rounded-3xl border border-gray-100 shadow-sm space-y-8 animate-tab-switch">
          {/* LOGO UPLOAD */}
          <div className="bg-slate-50/80 p-5 rounded-2xl border border-slate-200/60">
            <label className="block text-xs font-black uppercase tracking-wider text-gray-700 mb-3">Shop Logo</label>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
              <div className="relative group">
                {currentData.logo_url ? (
                  <div className="w-24 h-24 rounded-2xl overflow-hidden shadow-md border-4 border-white bg-white">
                    <img src={currentData.logo_url} alt="Logo" className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div className="w-24 h-24 rounded-2xl border-2 border-dashed border-gray-300 flex items-center justify-center bg-white shadow-inner">
                    <ImageIcon className="w-8 h-8 text-gray-300" />
                  </div>
                )}
              </div>
              <div>
                <input
                  type="file"
                  ref={logoInputRef}
                  onChange={(e) => handleLogoUpload(e.target.files[0])}
                  accept="image/*"
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => logoInputRef.current.click()}
                  className="px-6 py-2.5 bg-primary/10 text-primary hover:bg-primary hover:text-white rounded-xl font-bold transition-all text-sm"
                >
                  {currentData.logo_url ? 'Change Logo' : 'Upload Logo'}
                </button>
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-2">Square ratio (200x200px) recommended</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-5">
              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2 pl-1">Store Name</label>
                <input 
                  type="text" 
                  name="business_name" 
                  defaultValue={currentData.business_name} 
                  onChange={handleInputChange} 
                  className="w-full px-5 py-3 bg-gray-50 border border-gray-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all font-medium" 
                />
              </div>
              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2 pl-1">Owner Full Name</label>
                <input 
                  type="text" 
                  name="owner_name" 
                  defaultValue={currentData.owner_name || currentData.name} 
                  onChange={handleInputChange} 
                  className="w-full px-5 py-3 bg-gray-50 border border-gray-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all font-medium" 
                />
              </div>
              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2 pl-1">Location</label>
                <select
                  key={`loc-${locations.length}-${currentData.location_id?._id || currentData.location_id || ''}`}
                  name="location_id"
                  defaultValue={currentData.location_id?._id || currentData.location_id || ''}
                  onChange={handleInputChange}
                  className="w-full px-5 py-3 bg-gray-50 border border-gray-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all font-medium text-gray-700"
                >
                  <option value="" disabled>Select a location</option>
                  {locations.map((loc) => (
                    <option key={loc._id} value={loc._id}>
                      {loc.name} {loc.type ? `(${loc.type})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="space-y-5">
              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2 pl-1">Business Mobile</label>
                <PhoneInputComponent 
                  defaultCountryCode={currentData.country_code} 
                  defaultPhoneNumber={currentData.phone_number} 
                  onChange={handleInputChange} 
                />
              </div>
              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2 pl-1">Email Address</label>
                <input 
                  type="email" 
                  name="email" 
                  defaultValue={currentData.email} 
                  onChange={handleInputChange} 
                  className="w-full px-5 py-3 bg-gray-50 border border-gray-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all font-medium" 
                />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2 pl-1">Store Description</label>
            <textarea 
              name="description" 
              defaultValue={currentData.description} 
              onChange={handleInputChange} 
              rows={4} 
              className="w-full px-5 py-3 bg-gray-50 border border-gray-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all font-medium resize-none" 
              placeholder="Tell customers about your shop..." 
            />
          </div>

          <div className="pt-6 border-t border-gray-100">
             <button type="button" onClick={togglePasswordFields} className="text-xs font-black text-primary uppercase tracking-widest hover:text-primary/70 transition-colors">
               {showPasswordFields ? '− Cancel Password Change' : '+ Update Security Password'}
             </button>

             {showPasswordFields && (
               <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6 p-6 bg-rose-50/50 rounded-3xl border border-rose-100 animate-in slide-in-from-top-4">
                  <div>
                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2 pl-1">Current Password</label>
                    <input type="password" ref={currentPasswordRef} className="w-full px-5 py-3 bg-white border border-rose-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-rose-500/20 transition-all font-medium" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2 pl-1">New Secure Password</label>
                    <input type="password" ref={newPasswordRef} className="w-full px-5 py-3 bg-white border border-rose-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-rose-500/20 transition-all font-medium" />
                  </div>
               </div>
             )}
          </div>
        </div>
      )}

      {/* TAB 2: IMAGES & GALLERY */}
      {activeTab === 'images' && (
        <div key="images" className="bg-white p-6 sm:p-8 rounded-3xl border border-gray-100 shadow-sm space-y-8 animate-tab-switch">
          <div className="space-y-6">
            <h3 className="text-base font-extrabold text-primary flex items-center gap-2 uppercase tracking-tight">
              <Camera className="w-5 h-5" />
              Store Gallery
            </h3>
            
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-4">
              {currentData.image_url?.map((image, index) => (
                <div key={index} className="relative group aspect-square rounded-2xl overflow-hidden shadow-sm border border-gray-100">
                  <img src={image} alt="Store" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="bg-rose-500 text-white rounded-full p-2 hover:bg-rose-600 transition-colors shadow-lg transform hover:scale-110"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ))}
              <div
                className="aspect-square border-2 border-dashed border-gray-200 rounded-2xl flex flex-col items-center justify-center cursor-pointer hover:bg-primary/5 hover:border-primary/30 transition-all group"
                onClick={() => fileInputRef.current.click()}
              >
                <div className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center group-hover:bg-primary/10 transition-colors mb-3">
                  <ImageIcon className="w-5 h-5 text-gray-400 group-hover:text-primary transition-colors" />
                </div>
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest group-hover:text-primary">Add Photos</span>
                <input type="file" ref={fileInputRef} onChange={(e) => handleImageUpload(e.target.files)} accept="image/*" multiple className="hidden" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 3: LOCATION & OPERATING HOURS */}
      {activeTab === 'location' && (
        <div key="location" className="bg-white p-6 sm:p-8 rounded-3xl border border-gray-100 shadow-sm space-y-8 animate-tab-switch">
          <div className="space-y-4">
            <h3 className="text-base font-extrabold text-gray-800 uppercase tracking-tight flex items-center gap-2">
              <MapPin className="w-5 h-5 text-primary" />
              Location Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2 pl-1">City</label>
                <input 
                  type="text" 
                  name="city" 
                  defaultValue={currentData.city} 
                  onChange={handleInputChange} 
                  className="w-full px-5 py-3 bg-gray-50 border border-gray-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all font-medium" 
                />
              </div>
              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2 pl-1">Pincode</label>
                <input 
                  type="text" 
                  name="pincode" 
                  defaultValue={currentData.pincode} 
                  onChange={handleInputChange} 
                  className="w-full px-5 py-3 bg-gray-50 border border-gray-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all font-medium" 
                />
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2 pl-1">Full Address</label>
              <textarea 
                name="address" 
                defaultValue={currentData.address} 
                onChange={handleInputChange} 
                rows={3} 
                className="w-full px-5 py-3 bg-gray-50 border border-gray-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all font-medium resize-none" 
              />
            </div>

            {/* GOOGLE MAPS LINK & COORDINATES EXTRACTION */}
            <div className="bg-slate-50/90 p-6 rounded-3xl border border-slate-200/80 space-y-5">
              {/* MAPS LINK INPUT */}
              <div>
                <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2">
                  MAPS LINK
                </label>
                <div className="relative flex items-center">
                  <input
                    type="text"
                    name="map_url"
                    value={mapUrlInput}
                    onChange={(e) => {
                      setMapUrlInput(e.target.value);
                      handleInputChange(e);
                    }}
                    placeholder="Paste Google Maps URL"
                    className="w-full pl-5 pr-28 py-3.5 bg-white border border-gray-200/90 rounded-2xl text-sm font-medium text-gray-800 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder:text-gray-400 shadow-sm"
                  />
                  <button
                    type="button"
                    onClick={() => handleExtractCoordinates(mapUrlInput)}
                    className="absolute right-2 bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-700 text-xs font-black uppercase tracking-wider px-4 py-2 rounded-xl transition-all shadow-sm active:scale-95 border border-blue-100"
                  >
                    Extract
                  </button>
                </div>
              </div>

              {/* LATITUDE & LONGITUDE INPUTS */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2">
                    LATITUDE
                  </label>
                  <input
                    type="number"
                    step="any"
                    name="latitude"
                    value={latitudeInput}
                    onChange={(e) => {
                      const val = e.target.value;
                      setLatitudeInput(val);
                      if (formDataRef) formDataRef.current.latitude = val ? parseFloat(val) : '';
                    }}
                    placeholder="15.XXXX"
                    className="w-full p-3.5 bg-white border border-gray-200/90 rounded-2xl text-sm font-medium text-gray-800 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder:text-gray-400 shadow-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2">
                    LONGITUDE
                  </label>
                  <input
                    type="number"
                    step="any"
                    name="longitude"
                    value={longitudeInput}
                    onChange={(e) => {
                      const val = e.target.value;
                      setLongitudeInput(val);
                      if (formDataRef) formDataRef.current.longitude = val ? parseFloat(val) : '';
                    }}
                    placeholder="73.XXXX"
                    className="w-full p-3.5 bg-white border border-gray-200/90 rounded-2xl text-sm font-medium text-gray-800 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder:text-gray-400 shadow-sm"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="pt-6 border-t border-gray-100 space-y-6">
            <h3 className="text-base font-extrabold text-gray-800 uppercase tracking-tight flex items-center gap-2">
              Store Hours & Operations
            </h3>
            
            <div className="bg-slate-50 p-6 rounded-[2rem] border border-slate-100">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2 pl-1">Opens At</label>
                  <input 
                    type="time" 
                    name="businessHours.openingTime" 
                    value={businessHoursState.openingTime} 
                    onChange={handleInputChange} 
                    className="w-full px-5 py-3 bg-white border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all font-bold text-primary" 
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2 pl-1">Closes At</label>
                  <input 
                    type="time" 
                    name="businessHours.closingTime" 
                    value={businessHoursState.closingTime} 
                    onChange={handleInputChange} 
                    className="w-full px-5 py-3 bg-white border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all font-bold text-primary" 
                  />
                </div>
              </div>

              <div>
                 <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-3 pl-1">Operating Days</label>
                 <div className="flex flex-wrap gap-3">
                   {daysOfWeek.map((day) => {
                     const isSelected = (currentData.businessHours?.days || []).includes(day);
                     return (
                       <label key={day} className="cursor-pointer group">
                        <input type="checkbox" name="days" value={day} checked={isSelected} onChange={handleDaysChange} className="hidden" />
                        <div className={`px-4 py-2.5 rounded-xl text-xs font-black transition-all border ${isSelected ? 'bg-primary text-white border-primary shadow-lg shadow-primary/20' : 'bg-white text-gray-500 border-gray-200 group-hover:border-primary/30 group-hover:bg-primary/5'}`}>
                          {day.toUpperCase()}
                        </div>
                       </label>
                     );
                   })}
                 </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* TAB 4: SHOP TAGS */}
      {activeTab === 'tags' && (
        <div key="tags" className="bg-white p-6 sm:p-8 rounded-3xl border border-gray-100 shadow-sm space-y-8 animate-tab-switch">
          {/* HEADER & SEARCH */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-gray-100 pb-6">
            <div>
              <div className="flex items-center gap-3">
                <h3 className="text-base font-extrabold text-gray-900 uppercase tracking-tight flex items-center gap-2">
                  <TagIcon className="w-5 h-5 text-primary" /> Shop Tags
                </h3>
                <span className="text-[11px] font-bold text-primary bg-primary/10 px-3 py-1 rounded-full border border-primary/20">
                  {selectedSecondaryTags.length} Selected
                </span>
              </div>
              <p className="text-xs text-gray-500 font-medium mt-1">
                Select tags for your shop features, categories, and specialities.
              </p>
            </div>

            {/* SEARCH INPUT */}
            <div className="relative w-full md:w-72">
              <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={tagSearchQuery}
                onChange={(e) => setTagSearchQuery(e.target.value)}
                placeholder="Search tags..."
                className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder:text-gray-400"
              />
            </div>
          </div>

          {isTagsLoading ? (
            <div className="py-12 flex flex-col items-center justify-center space-y-3 text-gray-400">
              <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin"></div>
              <p className="text-xs font-bold uppercase tracking-wider">Loading shop tags...</p>
            </div>
          ) : tagsList.length === 0 ? (
            <div className="py-12 text-center text-gray-400 bg-slate-50 rounded-2xl border border-dashed border-gray-200">
              <TagIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-xs font-bold">No shop tags found.</p>
            </div>
          ) : (
            <div>
              {(() => {
                const filteredTags = tagsList.filter(t => t.name.toLowerCase().includes(tagSearchQuery.toLowerCase()));
                if (filteredTags.length === 0) {
                  return <p className="text-xs text-gray-400 italic py-4">No tags match your search query.</p>;
                }
                return (
                  <div className="flex flex-wrap gap-2.5">
                    {filteredTags.map((tag) => {
                      const isSelected = selectedSecondaryTags.includes(tag._id);
                      return (
                        <button
                          key={tag._id}
                          type="button"
                          onClick={() => toggleSecondaryTag(tag._id)}
                          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all shadow-sm active:scale-95 border ${
                            isSelected
                              ? 'bg-primary text-white border-primary shadow-primary/20 scale-[1.02]'
                              : 'bg-slate-50 text-slate-700 hover:bg-slate-100/90 border-slate-200/80'
                          }`}
                        >
                          {isSelected ? <Check className="w-3.5 h-3.5 text-white" /> : <TagIcon className="w-3.5 h-3.5 text-gray-400" />}
                          <span>{tag.name}</span>
                        </button>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}

    </div>
  );
};

export default ShopProfile;
